import { useState } from "react";
import {
  FiUsers as Users,
  FiClock as Clock,
  FiVideo as Video,
  FiCalendar as Calendar,
  FiGlobe as Globe,
  FiCopy as Copy,
  FiCheck as Check,
} from "react-icons/fi";
import { Room } from "../../entities/room.entity";
import { UserSummary } from "../../types";
import { Badge } from "src/components/Badge/Badge";
import { displayDate, getDuration } from "src/utils/dateHandler.util";
import { Link, useLocation } from "react-router-dom";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";

type RoomHeaderProps = {
  room: Room;
  host: UserSummary | null;
};

const statusStyles: Record<string, string> = {
  INITIATED: "bg-yellow-100 text-yellow-800",
  SCHEDULED: "bg-blue-100 text-blue-800",
  ACTIVE: "bg-green-100 text-green-800",
  ENDED: "bg-gray-100 text-gray-800",
  CANCELLED: "bg-red-100 text-red-800",
  EXPIRED: "bg-orange-100 text-orange-800",
};

const categoryStyles: Record<string, string> = {
  CLASSROOM: "bg-indigo-100 text-indigo-800",
  COMMUNITY: "bg-purple-100 text-purple-800",
};

export const RoomHeader: React.FC<RoomHeaderProps> = ({ room, host }) => {
  const [isCopied, setIsCopied] = useState(false);
  const { search } = useLocation();
  const backToRooms = appendSearchToPath("/rooms", search);

  const handleCopy = () => {
    navigator.clipboard.writeText(room.id);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full -ml-24 -mb-24"></div>

      <div className="relative z-10">
        <Link
          to={backToRooms}
          className="inline-flex items-center rounded-xl bg-white bg-opacity-15 px-4 py-2 text-sm font-medium text-white no-underline transition hover:bg-opacity-25"
        >
          Back to Rooms
        </Link>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">{room.title || "Untitled Room"}</h1>
            {host && (
              <p className="text-white text-opacity-80">
                Hosted by <span className="font-semibold">{host.name}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Badge label={room.category} classes={categoryStyles[room.category]} />
            <Badge label={room.status} classes={statusStyles[room.status]} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3 bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20 relative group">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Video className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white text-opacity-80 mb-1">Room ID</p>
              <div className="flex items-center gap-2">
                <p className="font-mono text-sm font-semibold truncate" title={room.id}>
                  {room.id}
                </p>
                <button
                  onClick={handleCopy}
                  className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                  title="Copy Room ID"
                >
                  {isCopied ? (
                    <Check className="w-4 h-4 text-green-300" />
                  ) : (
                    <Copy className="w-3.5 h-3.5 text-white opacity-70 group-hover:opacity-100" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-white text-opacity-80 mb-1">Privacy</p>
              <p className="text-sm font-semibold">{room.privacy}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-white text-opacity-80 mb-1">Total Joined Users</p>
              <p className="text-sm font-semibold">{room.stats?.joinedUsers ?? 0} Users</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-white text-opacity-80 mb-1">Start Time</p>
              <p className="text-sm font-semibold">{displayDate(room.startTime)}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-white text-opacity-80 mb-1">End Time</p>
              <p className="text-sm font-semibold">
                {room.endTime ? displayDate(room.endTime) : "Active"}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-white text-opacity-80 mb-1">Total Duration</p>
              <p className="text-sm font-semibold">{getDuration(room.startTime, room.endTime)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
